"use client";

import { useState } from "react";
import { useSession, useSignIn } from "@/lib/useSignIn";
import ConnectModal from "./ConnectModal";
import DepositModal from "./DepositModal";

const short = (a: string) =>
  a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a;

const FAMILY_LABEL: Record<string, string> = {
  evm: "EVM",
  solana: "SOL",
  aptos: "APT",
};

const ConnectButton = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  const { session, isSignedIn, isLoading } = useSession();
  const { signOut } = useSignIn();

  if (isLoading) {
    return <div className="h-9 w-28 animate-pulse rounded-md bg-raised" aria-hidden="true" />;
  }

  if (!isSignedIn) {
    return (
      <>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-black transition-colors hover:bg-brand-glow"
        >
          Connect wallet
        </button>
        <ConnectModal open={modalOpen} onClose={() => setModalOpen(false)} />
      </>
    );
  }

  const active =
    session?.wallets.find((w) => w.id === session.active_wallet_id) ?? session?.wallets[0];

  return (
    <>
      <div className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-expanded={menuOpen}
          className="flex items-center gap-2 rounded-md border border-line bg-surface px-3 py-2 text-sm text-fg transition-colors hover:bg-raised"
        >
          <span className="h-2 w-2 rounded-full bg-buy" aria-hidden="true" />
          <span className="tnum">{active ? short(active.address) : "Account"}</span>
        </button>

        {menuOpen && (
          <>
            {/* Click-away layer, cheaper and more reliable than document listeners. */}
            <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
            <div className="absolute right-0 z-50 mt-1 w-64 rounded-md border border-line bg-surface p-2 shadow-xl">
              <p className="px-2 pb-1 text-[11px] uppercase tracking-wide text-fg-muted">
                Linked wallets
              </p>
              {session?.wallets.map((w) => (
                <div
                  key={w.id}
                  className="flex items-center justify-between gap-2 rounded px-2 py-1.5 text-xs"
                >
                  <span className="tnum text-fg-secondary">{short(w.address)}</span>
                  <span className="rounded bg-raised px-1.5 py-0.5 text-[10px] text-fg-muted">
                    {FAMILY_LABEL[w.chain_family] ?? w.chain_family}
                  </span>
                </div>
              ))}

              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  setDepositOpen(true);
                }}
                className="mt-1 w-full rounded px-2 py-1.5 text-left text-xs text-fg transition-colors hover:bg-raised"
              >
                ↓ Deposit
              </button>

              {/* Signing in with another wallet while authenticated LINKS it to this same
                  account, one user, many chains. */}
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  setModalOpen(true);
                }}
                className="mt-1 w-full rounded px-2 py-1.5 text-left text-xs text-fg-muted transition-colors hover:bg-raised hover:text-fg"
              >
                + Link another wallet
              </button>

              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  void signOut();
                }}
                className="mt-1 w-full rounded px-2 py-1.5 text-left text-xs text-sell transition-colors hover:bg-raised"
              >
                Sign out
              </button>
            </div>
          </>
        )}
      </div>
      <ConnectModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <DepositModal open={depositOpen} onClose={() => setDepositOpen(false)} />
    </>
  );
};

export default ConnectButton;
